<template>
	<div class="leases-due-widget">
		<div class="leases-due-widget__header">
			<h3 class="leases-due-widget__title">Leases Due in</h3>
			
			<themed-tooltip 
				placement="bottom"
				:show-delay="300"
				max-width="300px"
			>
				<button 
					class="leases-due-widget__info" 
					aria-label="More information about Leases Due"
					@mouseenter="loadTooltip('dashboard-leasesduein')"
				>
					<svg 
						width="14" 
						height="14" 
						viewBox="0 0 24 24" 
						fill="none" 
						stroke="currentColor" 
						stroke-width="2" 
						stroke-linecap="round" 
						stroke-linejoin="round"
					>
						<circle cx="12" cy="12" r="10"></circle>
						<line x1="12" y1="16" x2="12" y2="12"></line>
						<line x1="12" y1="8" x2="12.01" y2="8"></line>
					</svg>
				</button>
				<template #content>
					<div v-html="getTooltipContent('dashboard-leasesduein')"></div>
				</template>
			</themed-tooltip>
		</div>
		
		<!-- Controls below header -->
		<div class="leases-due-widget__controls">
			<!-- Year Filter Dropdown -->
			<div class="leases-due-widget__filter" ref="dropdown">
				<button 
					class="leases-due-widget__filter-button"
					@click="toggleDropdown"
					aria-label="Select time period"
				>
					<span>{{ selectedYears }} {{ selectedYears === 1 ? 'Year' : 'Years' }}</span>
					<svg class="leases-due-widget__filter-icon" viewBox="0 0 20 20" fill="currentColor">
						<path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
					</svg>
				</button>
				
				<div v-if="isDropdownOpen" class="leases-due-widget__dropdown">
					<button
						v-for="years in [1, 2, 3, 4, 5]"
						:key="years"
						class="leases-due-widget__dropdown-item"
						:class="{ 'leases-due-widget__dropdown-item--active': selectedYears === years }"
						@click="selectYearFilter(years)"
					>
						<span>{{ years }} {{ years === 1 ? 'Year' : 'Years' }}</span>
						<svg v-if="selectedYears === years" class="leases-due-widget__check-icon" viewBox="0 0 20 20" fill="currentColor">
							<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
						</svg>
					</button>
				</div>
			</div>

			<!-- Orientation Toggle Button -->
			<button 
				class="leases-due-widget__orientation-toggle"
				@click="toggleOrientation"
				:aria-label="isVertical ? 'Switch to horizontal view' : 'Switch to vertical view'"
			>
				<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
					<path d="M10 3L10 17M10 3L7 6M10 3L13 6M10 17L7 14M10 17L13 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
				</svg>
			</button>
		</div>
		
		<div class="leases-due-widget__content" :class="{ 'leases-due-widget__content--horizontal': isVertical }">
			<div class="leases-due-widget__stats" :class="{ 'leases-due-widget__stats--horizontal': isVertical }">
				<div class="leases-due-widget__stat">
					<!-- Horizontal bars: 1 line -->
					<div v-if="!isVertical" class="leases-due-widget__stat-value leases-due-widget__stat-value--inline">
						{{ formatCompactNumber(upcomingSquareFootage) }} <span class="leases-due-widget__stat-total">of {{ formatCompactNumber(totalSquareFootage) }} Sq. Ft.</span>
					</div>
					<!-- Vertical bars: 2 lines -->
					<div v-else class="leases-due-widget__stat-value">
						<div>{{ formatCompactNumber(upcomingSquareFootage) }}</div>
						<div class="leases-due-widget__stat-total">of {{ formatCompactNumber(totalSquareFootage) }} Sq. Ft.</div>
					</div>
					<div class="leases-due-widget__bar" :class="{ 'leases-due-widget__bar--horizontal': isVertical }">
						<div class="leases-due-widget__bar-fill leases-due-widget__bar-fill--sqft-active" :style="!isVertical ? { width: sqftActivePercentage + '%' } : { height: sqftActivePercentage + '%' }"></div>
						<div class="leases-due-widget__bar-fill leases-due-widget__bar-fill--sqft-inactive" :style="!isVertical ? { width: sqftInactivePercentage + '%' } : { height: sqftInactivePercentage + '%' }"></div>
					</div>
				</div>
				
				<div class="leases-due-widget__stat">
					<!-- Horizontal bars: 1 line -->
					<div v-if="!isVertical" class="leases-due-widget__stat-value leases-due-widget__stat-value--inline">
						{{ upcomingPropertyCount }} <span class="leases-due-widget__stat-total">of {{ totalPropertyCount }} Sites</span>
					</div>
					<!-- Vertical bars: 2 lines -->
					<div v-else class="leases-due-widget__stat-value">
						<div>{{ upcomingPropertyCount }}</div>
						<div class="leases-due-widget__stat-total">of {{ totalPropertyCount }} Sites</div>
					</div>
					<div class="leases-due-widget__bar" :class="{ 'leases-due-widget__bar--horizontal': isVertical }">
						<div class="leases-due-widget__bar-fill leases-due-widget__bar-fill--property-active" :style="!isVertical ? { width: propertyActivePercentage + '%' } : { height: propertyActivePercentage + '%' }"></div>
						<div class="leases-due-widget__bar-fill leases-due-widget__bar-fill--property-inactive" :style="!isVertical ? { width: propertyInactivePercentage + '%' } : { height: propertyInactivePercentage + '%' }"></div>
					</div>
				</div>

				<div class="leases-due-widget__stat">
					<!-- The bar below is filled by activeSitePercentage, so the figure
					     has to be the active count. Printing inactiveSiteCount here read
					     as "1 of 316" beside a bar that was 99.7% green. -->
					<!-- Horizontal bars: 1 line -->
					<div v-if="!isVertical" class="leases-due-widget__stat-value leases-due-widget__stat-value--inline">
						{{ activeSiteCount }} <span class="leases-due-widget__stat-total">of {{ totalSiteCount }} Sites Active</span>
					</div>
					<!-- Vertical bars: 2 lines -->
					<div v-else class="leases-due-widget__stat-value">
						<div>{{ activeSiteCount }}</div>
						<div class="leases-due-widget__stat-total">of {{ totalSiteCount }} Sites Active</div>
					</div>
					<div class="leases-due-widget__bar" :class="{ 'leases-due-widget__bar--horizontal': isVertical }">
						<div class="leases-due-widget__bar-fill leases-due-widget__bar-fill--site-inactive" :style="!isVertical ? { width: inactiveSitePercentage + '%' } : { height: inactiveSitePercentage + '%' }"></div>
						<div class="leases-due-widget__bar-fill leases-due-widget__bar-fill--site-active" :style="!isVertical ? { width: activeSitePercentage + '%' } : { height: activeSitePercentage + '%' }"></div>
					</div>
				</div>
			</div>
			
			<!-- Single shared legend at the bottom -->
			<div class="leases-due-widget__legend">
				<span class="leases-due-widget__legend-item"><span class="leases-due-widget__legend-dot leases-due-widget__legend-dot--orange"></span> Sq. Ft. Due</span>
				<span class="leases-due-widget__legend-item"><span class="leases-due-widget__legend-dot leases-due-widget__legend-dot--yellow"></span> Sites Due</span>
				<span class="leases-due-widget__legend-item"><span class="leases-due-widget__legend-dot leases-due-widget__legend-dot--green"></span> Active</span>
				<span class="leases-due-widget__legend-item"><span class="leases-due-widget__legend-dot leases-due-widget__legend-dot--red"></span> Inactive</span>
				<span class="leases-due-widget__legend-item"><span class="leases-due-widget__legend-dot leases-due-widget__legend-dot--gray"></span> Remaining</span>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment';
import ThemedTooltip from '../ThemedTooltip.vue';
import axios from 'axios';

export default {
	name: 'LeasesDueWidget',
	components: {
		ThemedTooltip
	},
	props: {
		siteData: {
			type: Array,
			required: true
		}
	},
	data() {
		return {
			selectedYears: 3,
			isDropdownOpen: false,
			isTooltipVisible: false,
			tooltipCache: {},
			isVertical: false // true = vertical bars, false = horizontal bars
		}
	},
	mounted() {
		document.addEventListener('click', this.handleClickOutside);
	},
	beforeDestroy() {
		document.removeEventListener('click', this.handleClickOutside);
	},
	computed: {
		availableSites() {
			return this.siteData.filter(site => 
				site.archived_at == null && moment(site.lease_end_date).isAfter(moment())
			);
		},
		filteredSites() {
			const months = this.selectedYears * 12;
			return this.availableSites.filter(site =>
				moment(site.lease_end_date).isBefore(moment().add(months, 'M'))
			);
		},
		totalSquareFootage() {
			return _.sumBy(this.availableSites, 'size') || 0;
		},
		upcomingSquareFootage() {
			return _.sumBy(this.filteredSites, 'size') || 0;
		},
		sqftActivePercentage() {
			if (this.totalSquareFootage === 0) return 0;
			return (this.upcomingSquareFootage / this.totalSquareFootage) * 100;
		},
		sqftInactivePercentage() {
			if (this.totalSquareFootage === 0) return 0;
			return ((this.totalSquareFootage - this.upcomingSquareFootage) / this.totalSquareFootage) * 100;
		},
		totalPropertyCount() {
			return this.availableSites.length;
		},
		upcomingPropertyCount() {
			return this.filteredSites.length;
		},
		propertyActivePercentage() {
			if (this.totalPropertyCount === 0) return 0;
			return (this.upcomingPropertyCount / this.totalPropertyCount) * 100;
		},
		propertyInactivePercentage() {
			if (this.totalPropertyCount === 0) return 0;
			return ((this.totalPropertyCount - this.upcomingPropertyCount) / this.totalPropertyCount) * 100;
		},
		totalSiteCount() {
			return this.siteData.length;
		},
		// "Active" has to mean the same thing here as in the two bars above, which
		// count against availableSites. Deriving it from archived_at alone put a
		// 315 and a 316 denominator side by side with nothing explaining why they
		// differed — and they would drift further apart the moment a lease expired.
		activeSiteCount() {
			return this.availableSites.length;
		},
		inactiveSiteCount() {
			return this.totalSiteCount - this.activeSiteCount;
		},
		inactiveSitePercentage() {
			if (this.totalSiteCount === 0) return 0;
			return (this.inactiveSiteCount / this.totalSiteCount) * 100;
		},
		activeSitePercentage() {
			if (this.totalSiteCount === 0) return 0;
			return (this.activeSiteCount / this.totalSiteCount) * 100;
		}
	},
	methods: {
		selectYearFilter(years) {
			this.selectedYears = years;
			this.isDropdownOpen = false;
			this.$emit('years-changed', years);
		},
		toggleDropdown() {
			this.isDropdownOpen = !this.isDropdownOpen;
		},
		closeDropdown() {
			this.isDropdownOpen = false;
		},
		toggleOrientation() {
			this.isVertical = !this.isVertical;
		},
		handleClickOutside(event) {
			if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
				this.closeDropdown();
			}
		},
		formatNumber(value) {
			if (!value) return '0';
			return new Intl.NumberFormat('en-US', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0
			}).format(value);
		},
		formatCompactNumber(value) {
			if (!value || value === 0) return '0';
			
			// Convert to millions with 1 decimal place
			if (value >= 1000000) {
				return (value / 1000000).toFixed(1) + 'M';
			}
			// Convert to thousands with 1 decimal place
			if (value >= 1000) {
				return (value / 1000).toFixed(1) + 'K';
			}
			
			return value.toString();
		},
		getTooltipContent(identifier) {
			if (this.tooltipCache[identifier]) {
				return this.tooltipCache[identifier];
			}
			return '<div style="display: flex; align-items: center; gap: 0.5rem;"><svg class="animate-spin" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Loading...</span></div>';
		},
		async loadTooltip(identifier) {
			if (!this.tooltipCache[identifier]) {
				this.$set(this.tooltipCache, identifier, '<div style="display: flex; align-items: center; gap: 0.5rem;"><svg class="animate-spin" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Loading...</span></div>');
				
				try {
					const response = await axios.get(`/api/tooltips/identifier/${identifier}`);
					this.$set(this.tooltipCache, identifier, response.data.content || 'Tooltip content not available');
				} catch (err) {
					console.error('Error loading tooltip:', err);
					if (err.response && err.response.status === 404) {
						this.$set(this.tooltipCache, identifier, 'Tooltip content is not yet available for this field.');
					} else {
						this.$set(this.tooltipCache, identifier, 'Failed to load information');
					}
				}
			}
		}
	}
}
</script>
